import { FC, useEffect, useMemo } from 'react';
import { Grid } from '@mui/system';
import { isEmpty, isNil } from 'lodash';
import { globalAction } from 'common/actions';
import { FUNDS_ADD_INVESTMENTS, FUNDS_INVESTMENTS_TITLE, FUNDS_NO_INVESTMENTS } from 'common/constants/funds';
import { useStore } from 'common/store';
import { MeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, CustomContainer, MessageBox, SectionHeading } from 'components';
import { GridSkeleton } from 'components/Grid';
import { useExportSignal } from 'features/pdfExport';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import { SCHEDULE_OF_INVESTMENTS_CONTAINER_CLASSNAME } from 'pages/Funds/common/constants/scheduleOfInvestments';
import CompanyInvestments from 'pages/Funds/pages/ScheduleOfInvestments/components/CompanyInvestments/CompanyInvestments';
import { useIntelligenceContext } from 'pages/Intelligence/IntelligenceContext';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { useGetFirmInvestments } from 'services/hooks/firms';
import { getNumberValue, getObjectValue } from 'utilities';

const ScheduleOfInvestments: FC = () => {
  const [storeValue, dispatch] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  const firmId = getObjectValue(firmInfo).id as number | undefined;

  const { selectedFundId } = useIntelligenceContext();
  const { selectedMeasurementDate } = useFirmMeasurementDateFilter();

  // CompanyInvestments needs a MeasurementDate-shaped object; the firm
  // measurement-date filter exposes a CompanyMeasurementDateName which is
  // structurally compatible (it has `slug` and `date`).
  const measurementDate = useMemo<MeasurementDate | undefined>(
    () => (selectedMeasurementDate ? (selectedMeasurementDate as unknown as MeasurementDate) : undefined),
    [selectedMeasurementDate]
  );

  const {
    data: firmInvestments,
    isFetching,
    isLoading,
  } = useGetFirmInvestments({
    firmId,
    fundId: selectedFundId ?? null,
    measurementDate,
  });

  useEffect(() => {
    dispatch?.(globalAction.showLoadingProgress(isLoading));
  }, [dispatch, isLoading]);

  let scheduleExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoading) {
    scheduleExportStatus = 'loading';
  } else if (isEmpty(firmInvestments?.companies)) {
    scheduleExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.SCHEDULE_OF_INVESTMENTS.id,
    status: scheduleExportStatus,
  });

  if (isNil(firmId) || isNil(measurementDate)) {
    return <GridSkeleton maxColumns={10} maxRows={4} />;
  }

  if (isEmpty(firmInvestments?.companies)) {
    if (isLoading) {
      return <GridSkeleton maxColumns={10} maxRows={4} />;
    }
    return <MessageBox fullWidth={false} tagline={FUNDS_ADD_INVESTMENTS} title={FUNDS_NO_INVESTMENTS} />;
  }

  return (
    <CustomCompactContainer className={SCHEDULE_OF_INVESTMENTS_CONTAINER_CLASSNAME}>
      <Grid size={{ xs: 12 }}>
        {!isLoading && <SectionHeading leftAligned title={FUNDS_INVESTMENTS_TITLE} />}

        <CustomContainer data-ref={PdfExportTargets.SCHEDULE_OF_INVESTMENTS.id}>
          {firmInvestments?.companies?.map(company => (
            <CompanyInvestments
              company={company}
              isLoading={isLoading}
              isFetching={isFetching}
              key={getNumberValue(company?.company_id)}
              measurementDate={measurementDate}
            />
          ))}
        </CustomContainer>
      </Grid>
    </CustomCompactContainer>
  );
};

export default ScheduleOfInvestments;
